import React, { useMemo, useState } from 'react';
import { normalizeAuditRow } from '../shared/auditDiff';

// ── Action badge config: icon + colour per action type ──────────────────────
// Tones tuned to the design (amber update, indigo submit, grey login, etc.)
// Display labels are exactly as specified in the audit_log design note.
const ACTION_CONFIG = {
  CREATE:   { label: 'Record created',       cls: 'bg-green-50 text-green-600',   icon: 'check-circle' },
  UPDATE:   { label: 'Record updated',       cls: 'bg-amber-50 text-amber-600',   icon: 'pencil' },
  SUBMIT:   { label: 'Submitted for review', cls: 'bg-indigo-50 text-indigo-500', icon: 'send' },
  VALIDATE: { label: 'Validated',            cls: 'bg-sky-50 text-sky-500',       icon: 'shield' },
  APPROVE:  { label: 'Approved',             cls: 'bg-green-50 text-green-600',   icon: 'check-circle' },
  REJECT:   { label: 'Rejected',             cls: 'bg-red-50 text-red-500',       icon: 'x-circle' },
  LOGIN:    { label: 'User login',           cls: 'bg-gray-100 text-gray-600',    icon: 'lock' },
  LOGOUT:   { label: 'User logout',          cls: 'bg-gray-100 text-gray-600',    icon: 'lock' },
  EXPORT:   { label: 'Data exported',        cls: 'bg-amber-50 text-amber-600',   icon: 'upload' },
  DELETE:   { label: 'Record deleted',       cls: 'bg-red-50 text-red-500',       icon: 'trash' },
};

export { ACTION_CONFIG };

const PAGE_SIZES = [10, 25, 50];

// ── Tiny inline icon set (no external dep) ──────────────────────────────────
function Icon({ name, className = 'w-3.5 h-3.5' }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', viewBox: '0 0 24 24', className };
  switch (name) {
    case 'check-circle': return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>;
    case 'pencil':       return <svg {...p}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>;
    case 'send':         return <svg {...p}><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>;
    case 'shield':       return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
    case 'x-circle':     return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>;
    case 'lock':         return <svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>;
    case 'upload':       return <svg {...p}><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 20h14" /></svg>;
    case 'trash':        return <svg {...p}><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>;
    case 'users':        return <svg {...p}><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><path d="M17 8a3 3 0 010 6" /></svg>;
    case 'monitor':      return <svg {...p}><rect x="3" y="4" width="18" height="12" rx="1" /><path d="M8 20h8M12 16v4" /></svg>;
    case 'doc':          return <svg {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>;
    case 'chevron':      return <svg {...p}><path d="M6 9l6 6 6-6" /></svg>;
    case 'arrow-right':  return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'swap':         return <svg {...p}><path d="M7 10l-3 3 3 3" /><path d="M4 13h12M17 14l3-3-3-3" /><path d="M20 11H8" /></svg>;
    default:             return null;
  }
}

// Avatar with initials (no image dependency)
function Avatar({ name }) {
  const safeName = String(name || 'System').trim() || 'System';
  const initials = safeName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
      {initials}
    </div>
  );
}

// Icon picker for the target-record cell, by target type
const targetIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('auth')) return 'monitor';
  if (t.includes('document')) return 'doc';
  return 'users';
};

const fmtDate = (iso) => {
  if (!iso) return { date: '-', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  return {
    date: d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

// ── Field-level diff (the expanded panel) ───────────────────────────────────
function ChangesPanel({ changes }) {
  if (!changes || changes.length === 0) {
    return (
      <div className="px-6 py-6 text-sm text-gray-400">No field changes recorded for this action.</div>
    );
  }
  return (
    <div className="px-4 py-5 sm:px-6">
      {/* Sub-header */}
      <div className="mb-3 flex flex-wrap items-center gap-x-8 gap-y-2">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-500">
            <Icon name="swap" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Changes Made</p>
            <p className="text-xs text-gray-400">Updated {changes.length} field{changes.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <span className="ml-auto inline-flex items-center gap-2 text-sm font-medium text-red-500">
          <span className="h-3.5 w-3.5 rounded bg-red-100" /> Previous Value
        </span>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-green-600">
          <span className="h-3.5 w-3.5 rounded bg-green-100" /> New Value
        </span>
      </div>

      {/* Diff table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="grid grid-cols-[1.2fr_1.4fr_auto_1.4fr] items-center border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700">
          <span>Field</span>
          <span className="text-center">Previous Value</span>
          <span />
          <span className="text-center">New Value</span>
        </div>
        {changes.map((c) => (
          <div key={c.field}
            className="grid grid-cols-[1.2fr_1.4fr_auto_1.4fr] items-center border-b border-gray-50 px-5 py-3.5 last:border-0">
            <span className="text-sm text-gray-600">{c.field}</span>
            <span className="flex justify-center">
              <span className="rounded-md bg-red-50 px-2.5 py-1 text-sm text-red-600">{c.previous}</span>
            </span>
            <span className="flex justify-center px-3 text-gray-300"><Icon name="arrow-right" className="w-4 h-4" /></span>
            <span className="flex justify-center">
              <span className="rounded-md bg-green-50 px-2.5 py-1 text-sm text-green-600">{c.next}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Footer pagination ───────────────────────────────────────────────────────
// Driven entirely by props from the server's own pagination metadata — no
// local slicing. `rows` passed to AuditLogTable is always just the one page
// the backend already returned; see AuditLog.jsx.
function Pagination({ page, pageCount, total, pageSize, onPage, onPageSize }) {
  // Page-number strip: 1 … current window … last  (design shows 1 2 3 4 5 … 25)
  const nums = [];
  const last = pageCount;
  const windowEnd = Math.min(5, last);
  for (let i = 1; i <= windowEnd; i++) nums.push(i);
  if (last > windowEnd) { nums.push('…'); nums.push(last); }

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const numBtn = (active) =>
    `flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm ${
      active ? 'border-green-600 bg-green-50 font-semibold text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-2 py-4">
      <p className="text-sm text-gray-500">Showing {from} to {to} of {total} audit entries</p>

      <div className="flex items-center gap-2">
        <button onClick={() => onPage(page - 1)} disabled={page === 1}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          <span className="rotate-90"><Icon name="chevron" className="w-4 h-4" /></span> Previous
        </button>

        {nums.map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} className="px-1 text-gray-400">…</span>
            : <button key={n} onClick={() => onPage(n)} className={numBtn(n === page)}>{n}</button>
        )}

        <button onClick={() => onPage(page + 1)} disabled={page === pageCount || pageCount === 0}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40">
          Next <span className="-rotate-90"><Icon name="chevron" className="w-4 h-4" /></span>
        </button>
      </div>

      <div className="relative">
        <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}
          className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-600 focus:border-green-500 focus:outline-none">
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} per page</option>)}
        </select>
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="chevron" className="w-3.5 h-3.5" /></span>
      </div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
// `rows` is always just the one page the backend already returned (see
// AuditLog.jsx) — pagination here is display-only, driven by `page`/`total`/
// `onPageChange`/`onPageSizeChange` from the server's own pagination
// metadata, not computed from a fully-loaded local array.
export default function AuditLogTable({ rows, page, pageSize, total, onPageChange, onPageSizeChange }) {
  const [expanded, setExpanded] = useState(null);

  React.useEffect(() => {
    setExpanded(null);
  }, [rows]);

  // Normalise to display shape (handles backend previous_state/new_state
  // JSONB or pre-shaped mock). Already reverse-chronological from the
  // backend (AuditController orders by created_at desc), so no client re-sort.
  const pageRows = useMemo(() => rows.map(normalizeAuditRow), [rows]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const th = 'px-6 py-4 text-left text-sm font-semibold text-gray-700';

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50/70">
              <th className="w-12 px-6 py-4">
                <input type="checkbox" className="h-4 w-4 rounded accent-green-600" aria-label="Select all" />
              </th>
              <th className={th}>Timestamp</th>
              <th className={th}>Actor Name</th>
              <th className={th}>Action Type</th>
              <th className={th}>Target Record</th>
              <th className={`${th} text-right`}>Changes</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-400">No audit entries found.</td></tr>
            ) : (
              pageRows.map((r) => {
                const cfg = ACTION_CONFIG[r.action] || { label: r.action, cls: 'bg-gray-100 text-gray-600', icon: 'doc' };
                const actionLabel = r.action_label ?? cfg.label ?? r.action;
                const { date, time } = fmtDate(r.timestamp);
                const isOpen = expanded === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-gray-100 align-middle">
                      <td className="px-6 py-5">
                        <input type="checkbox" className="h-4 w-4 rounded accent-green-600" aria-label={`Select ${r.id}`} />
                      </td>

                      {/* Timestamp */}
                      <td className="px-6 py-5">
                        <p className="text-sm font-semibold text-gray-900">{date}</p>
                        <p className="text-xs text-gray-400">{time}</p>
                      </td>

                      {/* Actor */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <Avatar name={r.actor_name} />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{r.actor_name}</p>
                            <p className="text-xs text-gray-400">{r.actor_role}</p>
                          </div>
                        </div>
                      </td>

                      {/* Action pill */}
                      <td className="px-6 py-5">
                        <span className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${cfg.cls}`}>
                          <Icon name={cfg.icon} /> {actionLabel}
                        </span>
                      </td>

                      {/* Target record */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-50 text-sky-400">
                            <Icon name={targetIcon(r.target_type)} />
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-sky-500">{r.target_label}</p>
                            <p className="text-xs text-gray-400">{r.target_type}</p>
                          </div>
                        </div>
                      </td>

                      {/* Changes toggle */}
                      <td className="px-6 py-5">
                        <div className="flex justify-end">
                          <button type="button" onClick={() => setExpanded(isOpen ? null : r.id)}
                            className="flex w-[150px] items-center justify-between rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            {isOpen ? 'Hide Changes' : 'View Changes'}
                            <span className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'}>
                              <Icon name="chevron" className="w-4 h-4 text-gray-400" />
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="border-t border-gray-100 bg-gray-50/40">
                        <td colSpan={6}><ChangesPanel changes={r.changes} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-100">
        <Pagination
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onPage={(p) => onPageChange(Math.min(Math.max(1, p), pageCount))}
          onPageSize={onPageSizeChange}
        />
      </div>
    </div>
  );
}
